"use client";

import type { FC } from "react";
import { useId, useState } from "react";
import { Alert, AlertDescription, Button, DialogV2, DialogV2Body, DialogV2Content, DialogV2Description, DialogV2Footer, DialogV2Header, DialogV2Title, Field, FieldLabel, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea } from "@akashnetwork/ui/components";
import { TriangleAlertIcon } from "lucide-react";
import type { DeploymentCloseReason, DeploymentCloseReasonInput } from "./closeDeploymentReasons";
import { DEPLOYMENT_CLOSE_REASONS, MAX_CLOSE_REASON_DETAILS_LENGTH, toCloseReasonInput } from "./closeDeploymentReasons";
export type CloseDeploymentTarget = {
  dseqs: string[];
  name?: string | null;
};
type Props = {
  target: CloseDeploymentTarget;
  onConfirm: (reason: DeploymentCloseReasonInput) => void;
  onCancel: () => void;
};
export const CloseDeploymentDialog: FC<Props> = ({
  target,
  onConfirm,
  onCancel
}) => {
  const [closeReason, setCloseReason] = useState<DeploymentCloseReason>();
  const [details, setDetails] = useState("");
  const fieldIds = {
    reason: useId(),
    details: useId()
  };
  const count = target.dseqs.length;
  const isClosingSeveral = count > 1;
  function confirmWithReason() {
    if (closeReason) onConfirm(toCloseReasonInput(closeReason, details));
  }
  return <DialogV2 open onOpenChange={isOpen => !isOpen ? onCancel() : undefined} data-sentry-element="DialogV2" data-sentry-component="CloseDeploymentDialog" data-sentry-source-file="CloseDeploymentDialog.tsx">
      <DialogV2Content className="max-w-md" data-sentry-element="DialogV2Content" data-sentry-source-file="CloseDeploymentDialog.tsx">
        <DialogV2Header data-sentry-element="DialogV2Header" data-sentry-source-file="CloseDeploymentDialog.tsx">
          <DialogV2Title data-sentry-element="DialogV2Title" data-sentry-source-file="CloseDeploymentDialog.tsx">{isClosingSeveral ? `Close ${count} deployments?` : "Close this deployment?"}</DialogV2Title>
          <DialogV2Description data-sentry-element="DialogV2Description" data-sentry-source-file="CloseDeploymentDialog.tsx">
            {isClosingSeveral ? <>Closing these {count} deployments settles their leases and permanently tears them down. This can&apos;t be undone.</> : <>
                Closing <span className="font-semibold text-foreground">{target.name || `deployment ${target.dseqs[0]}`}</span> settles its leases and
                permanently tears it down. This can&apos;t be undone.
              </>}
          </DialogV2Description>
        </DialogV2Header>

        <DialogV2Body className="flex flex-col gap-4" data-sentry-element="DialogV2Body" data-sentry-source-file="CloseDeploymentDialog.tsx">
          <Alert variant="destructive" className="bg-destructive/10 p-4 text-foreground" data-sentry-element="Alert" data-sentry-source-file="CloseDeploymentDialog.tsx">
            <TriangleAlertIcon className="h-4 w-4" aria-hidden="true" data-sentry-element="TriangleAlertIcon" data-sentry-source-file="CloseDeploymentDialog.tsx" />
            <AlertDescription data-sentry-element="AlertDescription" data-sentry-source-file="CloseDeploymentDialog.tsx">
              {isClosingSeveral ? "All services stop immediately and the deployment URLs stop resolving. Any unspent balance is returned to your wallet. You'll need to create new deployments to run these workloads again." : "All services stop immediately and the deployment URL stops resolving. Any unspent balance is returned to your wallet. You'll need to create a new deployment to run this workload again."}
            </AlertDescription>
          </Alert>

          <Field className="gap-2" data-sentry-element="Field" data-sentry-source-file="CloseDeploymentDialog.tsx">
            <FieldLabel htmlFor={fieldIds.reason} data-sentry-element="FieldLabel" data-sentry-source-file="CloseDeploymentDialog.tsx">
              {isClosingSeveral ? "Why are you closing these deployments?" : "Why are you closing this deployment?"}
            </FieldLabel>
            <Select value={closeReason} onValueChange={value => setCloseReason(value as DeploymentCloseReason)} data-sentry-element="Select" data-sentry-source-file="CloseDeploymentDialog.tsx">
              <SelectTrigger id={fieldIds.reason} data-sentry-element="SelectTrigger" data-sentry-source-file="CloseDeploymentDialog.tsx">
                <SelectValue placeholder="Select a reason" data-sentry-element="SelectValue" data-sentry-source-file="CloseDeploymentDialog.tsx" />
              </SelectTrigger>
              <SelectContent data-sentry-element="SelectContent" data-sentry-source-file="CloseDeploymentDialog.tsx">
                {DEPLOYMENT_CLOSE_REASONS.map(option => <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          {closeReason === "other" && <Field className="gap-2">
              <FieldLabel htmlFor={fieldIds.details}>Tell us more (optional)</FieldLabel>
              <Textarea id={fieldIds.details} rows={3} maxLength={MAX_CLOSE_REASON_DETAILS_LENGTH} value={details} onChange={event => setDetails(event.target.value)} />
            </Field>}
        </DialogV2Body>

        <DialogV2Footer className="flex items-center justify-end gap-2" data-sentry-element="DialogV2Footer" data-sentry-source-file="CloseDeploymentDialog.tsx">
          <Button type="button" variant="ghost" onClick={onCancel} data-sentry-element="Button" data-sentry-source-file="CloseDeploymentDialog.tsx">
            Cancel
          </Button>
          <Button type="button" variant="destructive" disabled={!closeReason} onClick={confirmWithReason} data-sentry-element="Button" data-sentry-source-file="CloseDeploymentDialog.tsx">
            {isClosingSeveral ? `Close ${count} deployments` : "Close deployment"}
          </Button>
        </DialogV2Footer>
      </DialogV2Content>
    </DialogV2>;
};